"use client";

import React, { memo, useMemo } from "react";
import ReactMarkdown, { type Components } from "react-markdown";
import remarkGfm from "remark-gfm";
import { CodeBlock } from "@/components/code-block";
import { CitationButton } from "./CitationButton";
import Link from "next/link";

interface CitationMarkdownProps {
  children: string;
  onCitationClick?: (citation: number) => void;
}

// Stable empty function to avoid new function identity each render
const noop = () => {};

// Custom text renderer that handles citation patterns
const createTextRenderer = (onCitationClick?: (citation: number) => void) => {
  return function TextWithCitations({ children }: any) {
    if (typeof children !== "string") return <>{children}</>;

    // Regex to match citation patterns like [1], [1,2], [1,2,3]
    const citationRegex = /\[(\d+(?:,\s*\d+)*)\]/g;

    const parts = [];
    let lastIndex = 0;

    while (true) {
      const match = citationRegex.exec(children);
      if (match === null) break;
      // Add text before citation
      if (match.index > lastIndex) {
        parts.push(children.slice(lastIndex, match.index));
      }

      // Parse citation numbers
      const citationText = match[1];
      const citations = citationText
        .split(/,\s*/)
        .map((num) => parseInt(num.trim(), 10));

      // Add citation buttons with wrapping support
      parts.push(
        <span
          key={`citation-group-${match.index}`}
          className="inline-flex items-baseline gap-[1px] flex-wrap"
        >
          {citations.map((citation, idx) => (
            <CitationButton
              key={`citation-${match.index}-${citation}`}
              citation={citation}
              onClick={onCitationClick ?? noop}
            />
          ))}
        </span>
      );

      lastIndex = match.index + match[0].length;
    }

    // Add remaining text
    if (lastIndex < children.length) {
      parts.push(children.slice(lastIndex));
    }

    return <>{parts}</>;
  };
};

const createComponents = (
  onCitationClick?: (citation: number) => void
): Partial<Components> => {
  const click = onCitationClick ?? noop;
  const TextRenderer = createTextRenderer(click);

  return {
    // @ts-expect-error
    code: CodeBlock,
    pre: ({ children }) => <>{children}</>,
    text: TextRenderer,
    p: ({ node, children, ...props }) => {
      // Process children to handle citations in paragraphs
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child === "string") {
          return <TextRenderer>{child}</TextRenderer>;
        }
        return child;
      });

      return (
        <p className="mb-0 leading-relaxed text-foreground" {...props}>
          {processedChildren}
        </p>
      );
    },
    li: ({ node, children, ...props }) => {
      // Process children to handle citations in list items
      const processedChildren = React.Children.map(children, (child) => {
        if (typeof child === "string") {
          return <TextRenderer>{child}</TextRenderer>;
        }
        return child;
      });

      return (
        <li className="py-1 text-foreground" {...props}>
          {processedChildren}
        </li>
      );
    },
    ol: ({ node, children, ...props }) => {
      return (
        <ol className="list-decimal list-outside ml-4" {...props}>
          {children}
        </ol>
      );
    },
    ul: ({ node, children, ...props }) => {
      return (
        <ul
          className="list-decimal list-outside ml-4 mt-0 marker:text-muted-foreground"
          {...props}
        >
          {children}
        </ul>
      );
    },
    strong: ({ node, children, ...props }) => {
      return (
        <span className="font-semibold text-foreground" {...props}>
          {children}
        </span>
      );
    },
    a: ({ node, children, ...props }) => {
      return (
        // @ts-expect-error
        <Link
          className="text-muted-foreground hover:text-foreground hover:underline transition-colors"
          target="_blank"
          rel="noreferrer"
          {...props}
        >
          {children}
        </Link>
      );
    },
    h1: ({ node, children, ...props }) => {
      return (
        <h1
          className="text-3xl font-semibold mt-2 m-1 text-foreground"
          {...props}
        >
          {children}
        </h1>
      );
    },
    h2: ({ node, children, ...props }) => {
      return (
        <h2 className="text-2xl font-semibold mt-1 text-foreground" {...props}>
          {children}
        </h2>
      );
    },
    h3: ({ node, children, ...props }) => {
      return (
        <h3 className="text-xl font-semibold mt-2 m-1 text-foreground" {...props}>
          {children}
        </h3>
      );
    },
    h4: ({ node, children, ...props }) => {
      return (
        <h4 className="text-lg font-semibold mt-2 m-1 text-foreground" {...props}>
          {children}
        </h4>
      );
    },
    h5: ({ node, children, ...props }) => {
      return (
        <h5
          className="text-base font-semibold mt-2 m-1 text-foreground"
          {...props}
        >
          {children}
        </h5>
      );
    },
    h6: ({ node, children, ...props }) => {
      return (
        <h6 className="text-sm font-semibold mt-2 m-1 text-foreground" {...props}>
          {children}
        </h6>
      );
    },
    table: ({ node, children, ...props }) => {
      return (
        <div className="overflow-x-auto my-1">
          <table
            className="w-full border-collapse border border-border table-auto"
            {...props}
          >
            {children}
          </table>
        </div>
      );
    },
    thead: ({ node, children, ...props }) => {
      return (
        <thead className="bg-muted/50" {...props}>
          {children}
        </thead>
      );
    },
    tbody: ({ node, children, ...props }) => {
      return <tbody {...props}>{children}</tbody>;
    },
    tr: ({ node, children, ...props }) => {
      return (
        <tr className="border-b border-border" {...props}>
          {children}
        </tr>
      );
    },
    th: ({ node, children, ...props }) => {
      return (
        <th
          className="border border-border px-2 py-1 text-left font-semibold text-xs bg-muted/30 text-foreground min-w-[80px] whitespace-nowrap"
          {...props}
        >
          {children}
        </th>
      );
    },
    td: ({ node, children, ...props }) => {
      return (
        <td
          className="border border-border px-2 py-1 text-xs text-foreground min-w-[60px] max-w-[120px] overflow-hidden"
          {...props}
        >
          <div className="whitespace-normal break-words">
            {children}
          </div>
        </td>
      );
    },
  };
};

const remarkPlugins = [remarkGfm];

export const CitationMarkdown = memo(function CitationMarkdown({
  children,
  onCitationClick,
}: CitationMarkdownProps) {
  const components = useMemo(
    () => createComponents(onCitationClick),
    [onCitationClick]
  );

  return (
    <ReactMarkdown remarkPlugins={remarkPlugins} components={components}>
      {children}
    </ReactMarkdown>
  );
});
